import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api/nocode';
import Card from '../../components/ui/Card';
import SafeIcon from '../../common/SafeIcon';
import { getCampStatus } from '../../utils/campLogic';

const DAYS = [
  { day: 1, title: 'The Christian', subtitle: 'Mindset & Calling' },
  { day: 2, title: 'The Creator', subtitle: 'Writing' },
  { day: 3, title: 'The Caretaker', subtitle: 'Publishing' },
  { day: 4, title: 'The Connector', subtitle: 'Marketing' },
  { day: 5, title: 'Compass Summary', subtitle: 'Review & Export' },
];

export default function Assembly() {
  const { user } = useAuth();
  const [contents, setContents] = useState({});
  const [settings, setSettings] = useState({});
  const [activeDay, setActiveDay] = useState(1);
  const [loading, setLoading] = useState(true);
  const [adminBypass, setAdminBypass] = useState(true);
  const location = useLocation();

  const { isAAP, currentTrailDay, windowOpen } = getCampStatus(user, settings);
  const isLockedOut = !windowOpen && !(user?.role === 'admin' && adminBypass);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [resContent, resSettings] = await Promise.all([
          api.get('academy_content_gwaacademy', { block: 'assembly' }),
          api.get('settings_gwaacademy')
        ]);
        
        const settingsData = Array.isArray(resSettings) ? resSettings : resSettings.data || [];
        const sMap = {};
        settingsData.forEach(s => {
          if (s.setting_key) sMap[s.setting_key] = s.value;
        });
        setSettings(sMap);

        const data = Array.isArray(resContent) ? resContent : resContent.data || [];
        const map = {};
        data.filter(item => item.is_active == 1 || item.is_active === true).forEach(c => {
          map[c.day] = c;
        });
        setContents(map);

        // Handle active day selection from URL
        const params = new URLSearchParams(location.search);
        const urlDay = parseInt(params.get('day'));
        
        if (urlDay && urlDay >= 1 && urlDay <= 5) {
          setActiveDay(urlDay);
        } else {
          setActiveDay(1);
        }
      } catch (err) {
        console.error("Failed to load assembly content", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [location.search]);

  if (loading) {
    return (
      <div className="p-8 text-center text-muted flex flex-col items-center justify-center min-h-[400px]">
        <SafeIcon name="Loader" className="animate-spin text-4xl text-amber mb-4" />
        <p>Loading Morning Lesson...</p>
      </div>
    );
  }

  if (isLockedOut) {
    return (
      <div className="max-w-4xl mx-auto py-12 px-4 animate-fadeIn">
        <Card className="text-center py-16 px-8 border-2 border-forest-green shadow-xl bg-[#EAF1E6]">
          <div className="w-24 h-24 bg-forest-green rounded-full flex items-center justify-center mx-auto mb-6 shadow-md text-white">
            <SafeIcon name="Video" className="text-5xl" />
          </div>
          <h1 className="text-3xl font-bold text-dark-green mb-4 font-poppins">Morning Lesson Access Expired</h1>
          <p className="text-lg text-forest-green font-medium mb-6 max-w-2xl mx-auto">
            {settings.replay_expiry_msg || "Free pass replay access has expired. Upgrade to All-Access to continue watching all academy sessions."}
          </p>
          <a href="/#/upgrade">
            <button className="bg-amber text-white font-bold px-8 py-3 rounded hover:bg-orange-600 transition-colors">
              Upgrade to All-Access
            </button>
          </a>
        </Card>
      </div>
    );
  }

  const isEarlyLocked = currentTrailDay === 0 && user?.early_access != 1 && !(user?.role === 'admin' && adminBypass);

  if (isEarlyLocked) {
    return (
      <div className="max-w-4xl mx-auto py-12 px-4 animate-fadeIn">
        <Card className="text-center py-16 px-8 border-2 border-tan shadow-sm">
          <div className="w-20 h-20 bg-amber/20 rounded-full flex items-center justify-center mx-auto mb-6 text-amber">
            <SafeIcon name="Clock" className="text-4xl" />
          </div>
          <h1 className="text-3xl font-bold text-dark-green mb-4 font-poppins">Academy Hasn't Started Yet</h1>
          <p className="text-lg text-muted mb-6 max-w-2xl mx-auto">
            Morning Lessons unlock at 7:00 AM Eastern Time each day of academy. Check back on Day 1 to watch the first session!
          </p>
        </Card>
      </div>
    );
  }

  const content = contents[activeDay];
  const now = new Date();
  const isContentTimeUnlocked = content?.unlock_time ? (now >= new Date(content.unlock_time)) : (currentTrailDay >= activeDay);
  const isUnlocked = (user?.role === 'admin' && adminBypass) || user?.early_access == 1 || isContentTimeUnlocked;
  const currentPersona = content?.persona_label || DAYS[activeDay - 1].title;
  const currentDayTitle = content?.day_title || DAYS[activeDay - 1].subtitle;

  return (
    <div className="max-w-6xl mx-auto flex flex-col lg:flex-row gap-8 pb-20 animate-fadeIn">
      {/* Sidebar Navigation */}
      <div className="w-full lg:w-72 shrink-0 space-y-4 no-print">
        <div className="bg-[#EAF1E6] border-l-4 border-forest-green p-4 rounded-r-md mb-6">
          <h1 className="text-xl font-bold text-dark-green flex items-center gap-2">
            <SafeIcon name="Video" className="text-amber" /> Morning Lesson
          </h1>
          <p className="text-xs mt-1 text-forest-green font-medium">Daily Kickoff</p>
        </div>

        {/* Admin Unlock Bar */}
        {user?.role === 'admin' && (
          <div className="p-3 bg-dark-green text-white rounded-md flex items-center justify-between text-xs">
            <span className="font-bold flex items-center gap-1">
              <SafeIcon name={adminBypass ? 'Unlock' : 'Lock'} className="text-amber" /> Admin View
            </span>
            <button
              onClick={() => setAdminBypass(!adminBypass)}
              className="bg-white/20 hover:bg-white/30 text-white px-2 py-1 rounded text-[11px] font-bold"
            >
              {adminBypass ? 'Unlocked' : 'Simulate Lock'}
            </button>
          </div>
        )}
        
        <nav className="space-y-2">
          {DAYS.map((d) => {
            const dayContent = contents[d.day];
            const dayTimeUnlocked = dayContent?.unlock_time ? (now >= new Date(dayContent.unlock_time)) : (currentTrailDay >= d.day);
            const unlocked = (user?.role === 'admin' && adminBypass) || user?.early_access == 1 || dayTimeUnlocked;
            const active = activeDay === d.day;
            const personaLabel = dayContent?.persona_label || d.title;
            const dayTitle = dayContent?.day_title || d.subtitle;

            return (
              <button
                key={d.day}
                onClick={() => setActiveDay(d.day)}
                className={`w-full text-left p-4 rounded-md border transition-all flex items-center justify-between
                  ${active ? 'bg-forest-green border-forest-green text-white shadow-md' : 
                    'bg-white border-tan hover:border-amber text-dark-green'}`}
              >
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider mb-1 opacity-80">Day {d.day}</div>
                  <div className={`font-bold ${active ? 'text-white' : 'text-dark-green'}`}>{personaLabel}</div>
                  <div className={`text-xs ${active ? 'text-tan' : 'text-muted'}`}>{dayTitle}</div>
                </div>
                <div>
                  {!unlocked ? (
                    <SafeIcon name="Lock" className="text-gray-400" />
                  ) : active ? (
                    <SafeIcon name="ChevronRight" />
                  ) : (
                    <SafeIcon name="CheckCircle" className="text-amber" />
                  )}
                </div>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 min-w-0">
        <div className="flex justify-between items-center bg-white border-b border-tan pb-4 mb-6 pt-2 no-print">
          <div>
            <h2 className="text-2xl font-bold text-dark-green">Day {activeDay}: {currentPersona}</h2>
            <p className="text-muted">{currentDayTitle}</p>
          </div>
        </div>

        {!isAAP && (
          <div className="bg-[#fdfaf6] border border-tan rounded-lg p-5 flex items-start gap-4 mb-8">
            <SafeIcon name="Info" className="text-amber mt-1 shrink-0" />
            <div className="text-sm text-dark-green leading-relaxed">
              <span className="font-bold">Replay Access:</span> Free pass holders have access to all session replays until{' '}
              <strong>{settings.replay_expiry
                ? new Date(settings.replay_expiry).toLocaleString('en-US', {
                    weekday: 'long', month: 'long', day: 'numeric',
                    hour: 'numeric', minute: '2-digit', timeZoneName: 'short'
                  })
                : 'the end of the replay window'}</strong>. Upgrade to All-Access for lifetime replays.
            </div>
          </div>
        )}

        {content && isUnlocked ? (
          <div className="space-y-8">
            <Card className="overflow-hidden border-2 border-forest-green shadow-md">
              <div className="p-6">
                <div className="mb-2 inline-flex items-center gap-2 text-xs font-bold text-forest-green uppercase tracking-widest bg-forest-green/10 px-3 py-1 rounded-full w-fit">
                  Day {activeDay}
                </div>
                <h2 className="text-2xl font-bold text-dark-green mb-6 font-poppins leading-tight">
                  {content.day_title || `Morning Lesson Day ${activeDay}`}
                </h2>
                
                {/* Video Area */}
                <div className="aspect-video bg-black rounded-md overflow-hidden relative shadow-inner group mb-6">
                  {content.video_embed ? (
                    <div 
                      className="w-full h-full [&>iframe]:w-full [&>iframe]:h-full border-0 absolute inset-0" 
                      dangerouslySetInnerHTML={{ __html: content.video_embed }} 
                    />
                  ) : content.video_url ? (
                    <iframe 
                      src={content.video_url} 
                      className="w-full h-full border-0 absolute inset-0"
                      allow="autoplay; fullscreen; picture-in-picture" 
                      allowFullScreen
                    ></iframe>
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-gray-500 flex-col gap-2">
                      <SafeIcon name="VideoOff" className="text-3xl" />
                      <span className="text-sm font-medium">Video currently unavailable</span>
                    </div>
                  )}
                </div>

                {/* Intro text */}
                {content.intro_text && (
                  <div className="text-gray-700 leading-relaxed mb-6 whitespace-pre-wrap">
                    {content.intro_text}
                  </div>
                )}

                {/* Featured Product */}
                {(content.product_headline || content.product_image_url || content.product_url) && (
                  <div className="bg-[#F1F6FE] p-6 rounded-lg border-2 border-[#DA7D3A] mb-8 flex flex-col sm:flex-row gap-8 items-center">
                    {content.product_image_url && (
                      <div className="w-full sm:w-1/2 flex justify-center">
                        <img src={content.product_image_url} alt="Featured Product" className="w-full max-w-sm rounded-lg shadow-md" />
                      </div>
                    )}
                    <div className={`w-full ${content.product_image_url ? 'sm:w-1/2' : ''} text-center sm:text-left`}>
                      <h3 className="text-2xl font-bold text-dark-green mb-3">{content.product_headline}</h3>
                      {content.product_description && (
                        <p className="text-gray-700 mb-5 text-base">{content.product_description}</p>
                      )}
                      {content.product_url && (
                        <div>
                          <a href={content.product_url} target="_blank" rel="noopener noreferrer">
                            <button className="bg-amber text-white font-bold px-8 py-3 rounded-lg hover:bg-orange-600 transition-colors shadow-sm text-lg">
                              {content.product_button_text || 'Grab It Now'}
                            </button>
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Bonus Videos */}
                {(() => {
                  let bVideos = [];
                  try {
                    bVideos = JSON.parse(content.bonus_videos || '[]');
                    bVideos = Array.isArray(bVideos) ? bVideos : [];
                  } catch(e) {}
                  
                  if (bVideos.length === 0) return null;

                  return (
                    <div className="mt-8 space-y-6 pt-6 border-t border-tan">
                      <h3 className="text-xl font-bold text-dark-green flex items-center gap-2">
                        <SafeIcon name="PlayCircle" className="text-amber" /> Bonus Content
                      </h3>
                      {bVideos.map((v, i) => (
                        <div key={i} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                          <div className="aspect-video bg-black rounded-md overflow-hidden relative shadow-inner mb-4 group">
                            {v.embed ? (
                              <div 
                                className="w-full h-full [&>iframe]:w-full [&>iframe]:h-full border-0 absolute inset-0" 
                                dangerouslySetInnerHTML={{ __html: v.embed }} 
                              />
                            ) : v.url ? (
                              <iframe src={v.url} className="w-full h-full border-0 absolute inset-0" allowFullScreen></iframe>
                            ) : (
                              <div className="absolute inset-0 flex items-center justify-center text-gray-500 flex-col gap-2">
                                <SafeIcon name="VideoOff" className="text-3xl" />
                                <span className="text-sm font-medium">Video currently unavailable</span>
                              </div>
                            )}
                          </div>
                          {v.description && <p className="text-gray-700 leading-relaxed">{v.description}</p>}
                        </div>
                      ))}
                    </div>
                  );
                })()}

              </div>
            </Card>
          </div>
        ) : (
          <Card className="text-center py-16 bg-gray-50 border-dashed border-2 animate-fadeIn relative overflow-hidden">
            <div className="w-20 h-20 bg-[#EDE0CC] rounded-full flex items-center justify-center mb-6 mx-auto mt-4">
              <SafeIcon name="Lock" className="w-9 h-9 text-[#2D5016]" />
            </div>
            <h3 className="text-2xl font-bold text-dark-green mb-2">Session Not Yet Open</h3>
            <p className="text-muted max-w-sm mx-auto leading-relaxed">
              This session unlocks at <strong>7:00 AM Eastern Time</strong> on Day {activeDay}.
            </p>
          </Card>
        )}
      </div>
    </div>
  );
}